import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BarChart3, TrendingUp, Award, Target } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentResults = () => {
  const { token } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/test-results/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(response.data);
      } catch (error) {
        console.error('Error fetching results:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [token]);

  const avgPercentage = results.length > 0 
    ? (results.reduce((acc, r) => acc + r.percentage, 0) / results.length).toFixed(1)
    : 0;

  return (
    <DashboardLayout title="My Results">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <BarChart3 className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-2xl font-bold">{results.length}</p>
            <p className="text-sm text-muted-foreground">Tests Taken</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">{avgPercentage}%</p>
            <p className="text-sm text-muted-foreground">Avg. Score</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Award className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{results.filter(r => r.percentage >= 80).length}</p>
            <p className="text-sm text-muted-foreground">Top Scores</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Target className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-2xl font-bold">{results.length > 0 ? Math.min(...results.map(r => r.rank || 999)) : '-'}</p>
            <p className="text-sm text-muted-foreground">Best Rank</p>
          </CardContent>
        </Card>
      </div>

      {/* Results List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-24"></CardContent>
            </Card>
          ))}
        </div>
      ) : results.length > 0 ? (
        <div className="space-y-4">
          {results.map((result) => (
            <Card key={result.id} className="card-hover" data-testid={`result-${result.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center shrink-0 ${
                    result.percentage >= 80 ? 'bg-green-100' : result.percentage >= 60 ? 'bg-yellow-100' : 'bg-red-100'
                  }`}>
                    <span className={`font-heading text-2xl font-bold ${
                      result.percentage >= 80 ? 'text-green-600' : result.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {result.percentage?.toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">Test #{result.test_id?.slice(-6)}</h4>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                      <span>Marks: {result.marks_obtained}</span>
                      <span>Date: {result.submitted_at?.split('T')[0]}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {result.rank && <Badge variant="secondary">Rank #{result.rank}</Badge>}
                    <Badge className={
                      result.percentage >= 80 ? 'bg-green-100 text-green-700' : 
                      result.percentage >= 60 ? 'bg-yellow-100 text-yellow-700' : 
                      'bg-red-100 text-red-700'
                    }>
                      {result.percentage >= 80 ? 'Excellent' : result.percentage >= 60 ? 'Good' : 'Needs Improvement'}
                    </Badge>
                  </div>
                </div>
                {result.analysis && (
                  <p className="mt-3 text-sm text-muted-foreground bg-muted p-3 rounded-lg">{result.analysis}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No Results Yet</h3>
            <p className="text-muted-foreground">Your test results will appear here after you complete tests.</p>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default StudentResults;
